import React, { useEffect } from 'react'
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form'
import CustomButton from '../shared/CustomButton';
import { FaSave } from 'react-icons/fa';
import CustomAsyncSelect from '../form_components/CustomAsyncSelect';
import { searchClients } from '../../services/clients';
import { searchSupervisors } from '../../services/users';

const SiteForm = ({
    site,
    isSaving,
    formAction
}) => {
    const {
        reset,
        register,
        handleSubmit,
        formState: {errors},
        setError,
        control
    } = useForm({
        defaultValues: site ? {
            id: site.id,
            client: {label: site.client.name, value: site.client.id},
            supervisor: {label: site.supervisor.name, value: site.supervisor.id},
            name: site.name,
            longitude: site.longitude,
            latitude: site.latitude,
            geo_fence_radius: site.geo_fence_radius
        } : {}
    });

    const resetForm = () => {
        reset({
            id: '',
            client: null,
            supervisor: null,
            name: '',
            longitude: '',
            latitude: '',
            geo_fence_radius: ''
        });
    }

    useEffect(() => {
        if (site) {
            reset({
                id: site.id,
                client: {label: site.client.name, value: site.client.id},
                supervisor: {label: site.supervisor.name, value: site.supervisor.id},
                name: site.name,
                longitude: site.longitude,
                latitude: site.latitude,
                geo_fence_radius: site.geo_fence_radius
            });
        } else {
            reset({
            id: '',
            client: null,
            supervisor: null,
            name: '',
            longitude: '',
            latitude: '',
            geo_fence_radius: ''
        });
        }
    }, [reset, site]);

    /**
     * search for clients 
     * @param {*} inputValue 
     * @returns 
     */
    const loadClients = async (inputValue) => {
        if (inputValue.length < 3) {
            return [];
        }

        try {
            const response = await searchClients(inputValue);
            return response.data.map((client) => ({label: client.name, value: client.id}))
        } catch (error){
            return [];
        }
    }

    /**
     * search for supervisors 
     * @param {*} inputValue 
     * @returns 
     */
    const loadSupervisors = async (inputValue) => {
        if (inputValue.length < 3) {
            return [];
        }

        try {
            const response = await searchSupervisors(inputValue);
            return response.data.map((supervisor) => ({label: supervisor.name, value: supervisor.id}));
        } catch (error) {
            return [];
        }
    }

    const onSubmit = async (data) => {
        try {
            const success = await formAction(data);
            if (success) {
                resetForm();
            }
        } catch (error) {
            if (error.response && error.response.status === 422){
                Object.keys(error.response.data.errors).forEach((field) => {
                    setError(field, {
                        type: 'manual',
                        message: error.response.data.errors[field][0]
                    });
                });
            } else {
                throw error; // error will be handled by saveSite() in Sites.jsx 
            }
        }
    }
  return (
    <Form onSubmit={handleSubmit(onSubmit)}>

        <Row>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='client'>Client</Form.Label>
                    <CustomAsyncSelect
                        placeholder='Client'
                        loadOptions={loadClients}
                        rules={{ required: 'Select client'}}
                        control={control}
                        name='client'
                    />

                </Form.Group>
            
            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='supervisor'>Supervisor</Form.Label>
                    <CustomAsyncSelect
                        placeholder='Supervisor'
                        loadOptions={loadSupervisors}
                        rules={{ required: 'Select site supervisor'}}
                        control={control}
                        name='supervisor'
                    />
                    
                </Form.Group>
            
            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='name'>Name</Form.Label>
                    <input type='text' autoFocus autoComplete='off' placeholder='Name'
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        {...register('name', {
                            required: 'Enter site\'s name'
                        })}
                    />
                    {errors.name && (
                        <div className="invalid-feedback">{errors.name.message}</div>
                    )}

                </Form.Group>
            
            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>
                    
                    <Form.Label  htmlFor='longitude'>Longitude</Form.Label>
                    <input type='number' autoComplete='off' placeholder='Longitude' step='any'
                        className={`form-control ${errors.longitude ? 'is-invalid' : ''}`}
                        {...register('longitude', {
                            required: 'Enter site\'s longitude coordinate',
                            valueAsNumber: true,
                            max: {
                                value: 180,
                                message: 'Longitude must be between -180 and 180'
                            },
                            min: {
                                value: -180,
                                message: 'Longitude must be between -180 and 180'
                            }
                        })}
                    />
                    {errors.longitude && (
                        <div className="invalid-feedback">{errors.longitude.message}</div>
                    )}
                    
                    </Form.Group>
            
            </Col>

        </Row>

        <Row>
            
            <Col xl={3} lg={4} md={6} sm={12}>
                
                <Form.Group>

                    <Form.Label htmlFor='latitude'>Latitude</Form.Label>
                    <input type='number' autoComplete='off' placeholder='Latitude' step='any'
                        className={`form-control ${errors.latitude ? 'is-invalid' : ''}`}
                        {...register('latitude', {
                            required: 'Enter site\'s latitude coordinate',
                            valueAsNumber: true,
                            min: {
                                value: -90,
                                message: 'Latitude must be between -90 and 90'
                            },
                            max: {
                                value: 90,
                                message: 'Latitude must be between -90 and 90'
                            }
                        })}
                    />
                    {errors.latitude && (
                        <div className="invalid-feedback">{errors.latitude.message}</div>
                    )}

                </Form.Group>
            
            </Col>

            <Col xl={3} lg={4} md={6} sm={12}>

                <Form.Group>

                    <Form.Label htmlFor='geo_fence_radius'>Geofence Radius(m)</Form.Label>
                    <input type='number' autoComplete='off' placeholder='Geofence Radius (m)'
                        className={`form-control ${errors.geo_fence_radius ? 'is-invalid' : ''}`}
                        {...register('geo_fence_radius', {
                            required: 'Enter the geofence radius for the site',
                            pattern: {
                                value: /^\d+$/,
                            },
                            min: {
                                value: 0,
                                message: 'Geo fence radius must be a non-negative integer',
                            },
                        })}
                    />
                    {errors.geo_fence_radius && (
                        <div className="invalid-feedback">{errors.geo_fence_radius.message}</div>
                    )}

                </Form.Group>
            
            </Col>

        </Row>

        <CustomButton
            isSaving={isSaving}
            title={isSaving ? 'Saving' : 'Save'}
            icon={<FaSave />}
        />

    </Form>
  )
}

export default SiteForm